/**
 * SupportChooserDialog — a small modal asking which support channel the user
 * wants: the APP support team, or a specific CLINIC.
 *
 * The customer has two separate ticket streams (Session 13): platform support
 * (`/support`) and clinic tickets (`/clinic-tickets`). Several entry points
 * (Dashboard, Service history) must let the user pick which one before
 * navigating, so the picker lives here as one shared component.
 *
 * This component is navigation-agnostic: it reports the choice via
 * `onChoose('app' | 'clinic')` and the parent decides where to go — a list
 * (Dashboard) or a pre-filled "new ticket" form (Service history). Keeping
 * navigation out of here is what lets the same dialog serve both.
 *
 * Unread badges (Session 18.5): each option shows how many unread messages are
 * waiting in that stream. It self-fetches the unread summary via the shared
 * ['unread-summary'] query (deduped with the dashboard's own copy), so badges
 * appear wherever the dialog is used without the caller wiring counts through.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(domain: 'app' | 'clinic') => void} onChoose
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Badge,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { notificationsApi } from '@/services/api';

export function SupportChooserDialog({ open, onClose, onChoose }) {
  const { t } = useTranslation();

  const { data: unreadResp } = useQuery({
    queryKey: ['unread-summary'],
    queryFn: () => notificationsApi.getUnreadSummary(),
  });
  const appUnread = unreadResp?.data?.support ?? 0;
  const clinicUnread = unreadResp?.data?.clinic ?? 0;

  // One option row: icon (with a red count badge) + title/description, and a
  // trailing "N new" chip when there are unread messages.
  const Option = ({ icon, count, title, description, onClick }) => (
    <ListItemButton onClick={onClick} sx={{ borderRadius: 1, mb: 1, alignItems: 'flex-start' }}>
      <ListItemIcon sx={{ mt: 0.5 }}>
        <Badge badgeContent={count} color="error" overlap="circular">
          {icon}
        </Badge>
      </ListItemIcon>
      <ListItemText
        primary={title}
        secondary={description}
        primaryTypographyProps={{ fontWeight: 600 }}
      />
      {count > 0 && (
        <Chip
          label={t('supportChooser.unread', { count })}
          color="error"
          size="small"
          sx={{ mt: 0.5, flexShrink: 0 }}
        />
      )}
    </ListItemButton>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pr: 6 }}>
        {t('supportChooser.title')}
        <IconButton
          onClick={onClose}
          aria-label={t('common.cancel')}
          size="small"
          sx={{ position: 'absolute', top: 12, insetInlineEnd: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <List disablePadding>
          <Option
            icon={<ContactSupportIcon color="primary" />}
            count={appUnread}
            title={t('supportChooser.app.title')}
            description={t('supportChooser.app.description')}
            onClick={() => onChoose('app')}
          />
          <Option
            icon={<StorefrontIcon color="primary" />}
            count={clinicUnread}
            title={t('supportChooser.clinic.title')}
            description={t('supportChooser.clinic.description')}
            onClick={() => onChoose('clinic')}
          />
        </List>
      </DialogContent>
    </Dialog>
  );
}

export default SupportChooserDialog;
